import React, { useState } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { getFilteredPinsForSensor } from '../../utils/devicePins';
import { generateEntityId } from '../../utils/deviceIds';

const DeviceSensorsPanel = ({ sensors, setSensors, deviceType, board }) => {
  const [currentSensorName, setCurrentSensorName] = useState('');
  const [currentSensorType, setCurrentSensorType] = useState('');
  const [currentSensorDataType, setCurrentSensorDataType] = useState('');
  const [currentSensorUnit, setCurrentSensorUnit] = useState('C');
  const [currentSensorPin, setCurrentSensorPin] = useState('');

  const addSensor = () => {
    const isDHT = currentSensorType === 'DHT11' || currentSensorType === 'DHT22';
    if (currentSensorName && currentSensorType && currentSensorPin && (!isDHT || currentSensorDataType)) {
      setSensors([...sensors, {
        sensorID: generateEntityId(),
        name: currentSensorName,
        sensorType: currentSensorType,
        pin: currentSensorPin,
        dataType: isDHT ? currentSensorDataType : '',
        unit: (isDHT && currentSensorDataType === 'temperature') ? currentSensorUnit : 'C',
      }]);
      setCurrentSensorName('');
      setCurrentSensorType('');
      setCurrentSensorDataType('');
      setCurrentSensorUnit('C');
      setCurrentSensorPin('');
    }
  };

  const removeSensor = (index) => {
    setSensors(sensors.filter((_, i) => i !== index));
  };

  const updateSensor = (index, field, value) => {
    const updatedSensors = [...sensors];
    updatedSensors[index] = { ...updatedSensors[index], [field]: value };
    setSensors(updatedSensors);
  };

  const pinOptions = (sensorType, excludeIndex = -1) => getFilteredPinsForSensor({
    sensors,
    deviceType,
    board,
    sensorType,
    excludeIndex,
  });

  return (
    <div className="setup-panel">
      <h4 className="mb-3">Manage Sensors</h4>
      <Form.Group className="mb-3">
        <Form.Label>Add Sensor</Form.Label>
        <div className="d-flex gap-3 mb-2 flex-wrap">
          <Form.Control
            type="text"
            value={currentSensorName}
            onChange={(e) => setCurrentSensorName(e.target.value)}
            placeholder="Name (e.g., Outdoor Temp)"
            className="flex-grow-1"
          />
          <Form.Select
            value={currentSensorType}
            onChange={(e) => {
              setCurrentSensorType(e.target.value);
              if (e.target.value !== 'DHT11' && e.target.value !== 'DHT22') {
                setCurrentSensorDataType('');
              }
            }}
            className="flex-grow-1"
          >
            <option value="">Select sensor type</option>
            <option value="DHT11">DHT11</option>
            <option value="DHT22">DHT22</option>
            <option value="soil_moisture">Soil Moisture</option>
          </Form.Select>
          {(currentSensorType === 'DHT11' || currentSensorType === 'DHT22') && (
            <>
              <Form.Select
                value={currentSensorDataType}
                onChange={(e) => setCurrentSensorDataType(e.target.value)}
                className="flex-grow-1"
              >
                <option value="">Select data type</option>
                <option value="temperature">Temperature</option>
                <option value="humidity">Humidity</option>
              </Form.Select>
              {currentSensorDataType === 'temperature' && (
                <Form.Select
                  value={currentSensorUnit}
                  onChange={(e) => setCurrentSensorUnit(e.target.value)}
                  className="flex-grow-1"
                >
                  <option value="C">Celsius (°C)</option>
                  <option value="F">Fahrenheit (°F)</option>
                </Form.Select>
              )}
            </>
          )}
          <Form.Select value={currentSensorPin} onChange={(e) => setCurrentSensorPin(e.target.value)}>
            <option value="">Select pin</option>
            {currentSensorType && pinOptions(currentSensorType).map((pin) => (
              <option key={pin} value={pin}>{pin}</option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={addSensor}>Add Sensor</Button>
        </div>
      </Form.Group>

      {sensors.length > 0 && (
        <div className="mb-3">
          <h4 className="mb-3">Current Sensors:</h4>
          <ListGroup>
            {sensors.map((sensor, index) => (
              <ListGroup.Item key={index} className="d-flex gap-3 align-items-center flex-wrap">
                <Form.Control
                  type="text"
                  value={sensor.name}
                  onChange={(e) => updateSensor(index, 'name', e.target.value)}
                  placeholder="Name"
                  className="w-auto flex-grow-1"
                />
                <Form.Select
                  value={sensor.sensorType}
                  onChange={(e) => {
                    updateSensor(index, 'sensorType', e.target.value);
                    if (e.target.value !== 'DHT11' && e.target.value !== 'DHT22') {
                      updateSensor(index, 'dataType', '');
                      updateSensor(index, 'unit', '');
                    }
                  }}
                  className="w-auto flex-grow-1"
                >
                  <option value="DHT11">DHT11</option>
                  <option value="DHT22">DHT22</option>
                  <option value="soil_moisture">Soil Moisture</option>
                </Form.Select>
                {(sensor.sensorType === 'DHT11' || sensor.sensorType === 'DHT22') && (
                  <>
                    <Form.Select
                      value={sensor.dataType || ''}
                      onChange={(e) => updateSensor(index, 'dataType', e.target.value)}
                      className="w-auto flex-grow-1"
                    >
                      <option value="">Select data type</option>
                      <option value="temperature">Temperature</option>
                      <option value="humidity">Humidity</option>
                    </Form.Select>
                    {sensor.dataType === 'temperature' && (
                      <Form.Select
                        value={sensor.unit || 'C'}
                        onChange={(e) => updateSensor(index, 'unit', e.target.value)}
                        className="w-auto flex-grow-1"
                      >
                        <option value="C">Celsius (°C)</option>
                        <option value="F">Fahrenheit (°F)</option>
                      </Form.Select>
                    )}
                  </>
                )}
                <Form.Select
                  value={sensor.pin}
                  onChange={(e) => updateSensor(index, 'pin', e.target.value)}
                  className="w-auto"
                >
                  <option value="">Select pin</option>
                  {pinOptions(sensor.sensorType, index).map((pin) => (
                    <option key={pin} value={pin}>{pin}</option>
                  ))}
                </Form.Select>
                <Button variant="outline-danger" onClick={() => removeSensor(index)}>Remove</Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default DeviceSensorsPanel;